export const PERMISSION_MODULES = [
  'dashboard',
  'clients',
  'consignors',
  'consignees',
  'branches',
  'staff',
  'drivers',
  'helpers',
  'vehicles',
  'gps_tracking',
  'trips',
  'fuel',
  'maintenance',
  'puncture',
  'invoices',
  'bilty',
  'documents',
  'reports',
  'crm',
  'teams',
  'integrations',
  'layouts',
  'activity',
  'settings',
  'roles',
] as const;

export type PermissionModule = typeof PERMISSION_MODULES[number];

export const PERMISSION_ACTIONS = [
  'create',
  'view',
  'edit',
  'delete',
  'import',
  'export',
  'approve',
  'print',
] as const;

export type PermissionAction = typeof PERMISSION_ACTIONS[number];

export const MODULE_LABELS: Record<PermissionModule, string> = {
  dashboard: 'Home',
  clients: 'Clients',
  consignors: 'Senders',
  consignees: 'Receivers',
  branches: 'Branches',
  staff: 'Staff',
  drivers: 'Drivers',
  helpers: 'Helpers',
  vehicles: 'Vehicles',
  gps_tracking: 'Live Map (GPS)',
  trips: 'Trips',
  fuel: 'Fuel',
  maintenance: 'Service / Repair',
  puncture: 'Puncture',
  invoices: 'Bills',
  bilty: 'Bilty (LR)',
  documents: 'Documents',
  reports: 'Reports',
  crm: 'CRM',
  teams: 'Teams',
  integrations: 'Connected Apps',
  layouts: 'Form Layout',
  activity: 'Change History',
  settings: 'Settings',
  roles: 'Staff & Login',
};

/** Friendly names for permission checkboxes */
export const ACTION_LABELS: Record<PermissionAction, string> = {
  create: 'Add',
  view: 'See',
  edit: 'Change',
  delete: 'Remove',
  import: 'Upload',
  export: 'Download',
  approve: 'Approve',
  print: 'Print',
};

/** Modules shown in tenant role/team permission editor */
export const TENANT_PERMISSION_MODULES = [...PERMISSION_MODULES];

/** Friendly group titles (no jargon like Operations / Organization) */
export const PERMISSION_GROUP_LABELS: Record<string, string> = {
  Main: 'Home',
  Operations: 'Trips & Bilty',
  Parties: 'Clients',
  Fleet: 'Vehicles & GPS',
  Maintenance: 'Fuel & Repair',
  Documents: 'Documents',
  Organization: 'Office',
  Settings: 'Settings',
};

export const PERMISSION_MODULE_GROUPS: Record<string, PermissionModule[]> = {
  Main: ['dashboard'],
  Operations: ['trips', 'bilty'],
  Parties: ['clients'],
  Fleet: ['vehicles', 'gps_tracking', 'drivers', 'helpers'],
  Maintenance: ['fuel', 'maintenance', 'puncture'],
  Documents: ['documents'],
  Organization: ['branches', 'roles', 'teams'],
  Settings: ['integrations', 'layouts', 'activity', 'settings', 'reports'],
};

/** Sub-permissions nested under a parent module in the role editor */
export const PERMISSION_SUB_MODULES: Partial<Record<PermissionModule, PermissionModule[]>> = {
  bilty: ['consignors', 'consignees', 'invoices'],
  roles: ['staff'],
};
export const emptyPermissions = (): Record<string, Record<string, boolean>> =>
  TENANT_PERMISSION_MODULES.reduce((acc, mod) => {
    acc[mod] = PERMISSION_ACTIONS.reduce((actions, action) => {
      actions[action] = false;
      return actions;
    }, {} as Record<string, boolean>);
    return acc;
  }, {} as Record<string, Record<string, boolean>>);

/** Map route/module keys to permission module (custom CRM modules use crm) */
export const resolvePermissionModule = (moduleKey: string): PermissionModule | string => {
  if (moduleKey === 'bilty_items') return 'bilty';
  if (moduleKey === 'vehicle_types' || moduleKey === 'vehicle_makes' || moduleKey === 'vehicle_models') {
    return 'vehicles';
  }
  if (moduleKey === 'vehicle_vendors') return 'vehicles';
  if (moduleKey === 'vehicle_documents') return 'documents';
  if (moduleKey === 'gps_devices' || moduleKey === 'gps_alerts' || moduleKey === 'gps_reports') {
    return 'gps_tracking';
  }
  if (PERMISSION_MODULES.includes(moduleKey as PermissionModule)) {
    return moduleKey as PermissionModule;
  }
  return 'crm';
};

export const canViewSidebarModule = (
  moduleKey: string,
  moduleType: 'core' | 'custom',
  hasPermission: (module: string, action?: string) => boolean,
) => {
  const permKey = moduleType === 'custom' ? 'crm' : resolvePermissionModule(moduleKey);
  return hasPermission(String(permKey), 'view');
};
